/**
 * Reports tab — a filterable, printable usage dashboard for one sub-account.
 *
 * The report is NOT auto-loaded: the operator picks a date range + Type and clicks Generate; only then
 * is the (12-month-capped) server query fired. KPIs, trend and heatmaps come from the daily metrics
 * tables (account-level, wall-clock minutes); the per-user table comes from the history tables and can
 * undercount the headline totals (coverage footnote). Active hosts (KPI + per-period bars) also come
 * from the history tables: distinct hosts over the whole range for the tile, distinct within each
 * period for the bars — the tile is NOT the sum of the bars. History starts later than the metrics, so
 * pre-coverage bars are null ("no data") and a FeedNote says so (it prints). Bar charts show the value
 * on every bar so the numbers are readable at a glance. Print → PDF uses the browser dialog + print.css.
 *
 * Zoom Phone (wave 5, owner ruling 2026-09-19 — moved here from the Usage tab: Usage = licences, Reports
 * = activity): call KPIs + inbound/outbound per period over the SAME range and bucket keys, with the
 * same coverage rule as Active hosts (pre-coverage periods = no data + a FeedNote). A phone day is
 * ZPCD's UTC day, not a Bangkok day (API reports/service.py::_phone, W5-P1), so the UI makes no Bangkok
 * claim for phone. Shown only for an account with phone data (has_phone_data), whatever the
 * meetings/webinars Type filter — and also when the range has no meeting activity (a phone-only account).
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Printer, BarChart3 } from "lucide-react";
import {
  Button,
  Select,
  Tabs,
  DateRangePicker,
  type DateRange,
  ErrorState,
  EmptyState,
  SkeletonRows,
} from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatDate, formatInt } from "@/lib/format";
import type {
  CustomerReport,
  ReportHeatmapCell,
  ReportPhone,
  ReportTrendBucket,
  ReportType,
} from "@/api/types";
import { BarChart, StackedBarChart, type ChartBar } from "../charts";
import { TARGET_FOCUS, useChartTooltip } from "../chartTooltip";
import { KpiTile, Section, FeedNote, monthShort } from "../display";
import { useCustomerReports, useCustomerReportsInsights } from "../useCustomer";
import { ReportTopUsersTable } from "./ReportTopUsers";
import { InsightsReportBody } from "./ReportInsights";

/** Which of the two Reports-tab reports is showing. The Standard report (ReportBody below) is
 *  UNCHANGED by this addition — same hook, same component, same styling; this only decides which of
 *  the two result sets is rendered below the shared Period/Type filter bar. */
type ReportKind = "standard" | "insights";

const DAY = 86_400_000;
const isoDate = (d: Date) => d.toISOString().slice(0, 10);

interface Applied extends DateRange {
  type: ReportType;
}

export function ReportsTab({ accountId }: { accountId: string }) {
  const { t } = useTranslation();

  const today = useMemo(() => new Date(), []);
  const todayStr = isoDate(today);
  const floorStr = isoDate(new Date(today.getTime() - 366 * DAY));
  const [range, setRange] = useState<DateRange>({
    start: isoDate(new Date(today.getTime() - 365 * DAY)),
    end: todayStr,
  });
  const [type, setType] = useState<ReportType>("both");
  // Which of the two reports is showing. Independent of `applied`/`range`/`type` above — both reports
  // share the same Period/Type filter bar, so switching this tab never re-prompts for a range.
  const [kind, setKind] = useState<ReportKind>("standard");
  // Nothing loads until the operator confirms a range. `applied` is the committed query; editing the
  // controls above does NOT refetch until Generate is pressed again.
  const [applied, setApplied] = useState<Applied | null>(null);

  // Each hook only actually fetches when its OWN kind is active (`enabled`) — switching the tab never
  // fires the other report's request a second time, and the Standard report's own hook call/behaviour
  // is unchanged from before this file grew a second report.
  const report = useCustomerReports(
    accountId,
    applied ?? { type },
    applied != null && kind === "standard",
  );
  const insights = useCustomerReportsInsights(
    accountId,
    applied ?? { type },
    applied != null && kind === "insights",
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Report-kind picker — hidden in print (the printed page is always whichever kind was on screen). */}
      <Tabs
        label={t("customer.reportsInsights.kindLabel")}
        value={kind}
        onValueChange={(v) => setKind(v as ReportKind)}
        items={[
          { value: "standard", label: t("customer.reportsInsights.kindStandard") },
          { value: "insights", label: t("customer.reportsInsights.kindInsights") },
        ]}
        className="print:hidden"
      />

      {/* Filter bar — hidden in print. Shared by BOTH report kinds. */}
      <div className="flex flex-wrap items-end gap-3 print:hidden">
        <div className="flex flex-col gap-1">
          <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
            {t("customer.reports.period")}
          </span>
          <DateRangePicker
            value={range}
            onChange={setRange}
            min={floorStr}
            max={todayStr}
            aria-label={t("customer.reports.period")}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
            {t("customer.reports.type")}
          </span>
          <Select
            value={type}
            onChange={(v) => setType(v as ReportType)}
            options={[
              { value: "both", label: t("customer.reports.typeBoth") },
              { value: "meetings", label: t("customer.reports.typeMeetings") },
              { value: "webinars", label: t("customer.reports.typeWebinars") },
            ]}
            className="min-w-48"
            aria-label={t("customer.reports.type")}
          />
        </div>
        <Button variant="secondary" size="sm" onClick={() => setApplied({ ...range, type })}>
          <BarChart3 className="size-4" aria-hidden />
          {t("customer.reports.generate")}
        </Button>
        {applied != null && (kind === "standard" ? report.data : insights.data) && (
          <Button variant="secondary" size="sm" onClick={() => window.print()}>
            <Printer className="size-4" aria-hidden />
            {t("customer.reports.print")}
          </Button>
        )}
      </div>

      {applied == null ? (
        <EmptyState
          icon={<BarChart3 className="size-8" />}
          title={t("customer.reports.choosePrompt")}
          body={t("customer.reports.chooseBody")}
        />
      ) : kind === "standard" ? (
        report.isPending ? (
          <SkeletonRows rows={6} />
        ) : report.isError ? (
          <ErrorState error={report.error} onRetry={() => void report.refetch()} />
        ) : (
          <ReportBody data={report.data} type={applied.type} />
        )
      ) : insights.isPending ? (
        <SkeletonRows rows={6} />
      ) : insights.isError ? (
        <ErrorState error={insights.error} onRetry={() => void insights.refetch()} />
      ) : (
        <InsightsReportBody data={insights.data} type={applied.type} />
      )}
    </div>
  );
}

function ReportBody({ data, type }: { data: CustomerReport; type: ReportType }) {
  const { t } = useTranslation();
  const { meta, kpis, trend, heatmap, top_users } = data;

  const periodLabel = `${formatDate(meta.start)} – ${formatDate(meta.end)}`;
  const eventsLabel =
    type === "webinars"
      ? t("customer.reports.kpiWebinars")
      : type === "meetings"
        ? t("customer.reports.kpiMeetings")
        : t("customer.reports.kpiEvents");
  const eventsValue =
    type === "webinars"
      ? kpis.total_webinars
      : type === "meetings"
        ? kpis.total_meetings
        : kpis.total_events;

  if (kpis.total_events === 0) {
    return (
      <div className="report-root flex flex-col gap-4">
        <PrintHeader
          account={meta.account_name}
          number={meta.account_number}
          period={periodLabel}
        />
        <EmptyState
          title={t("customer.reports.thinTitle")}
          body={t("customer.reports.emptyRange")}
        />
        {/* A phone-only account has no meeting activity but still has calls to report. */}
        <PhoneSection phone={data.phone} meta={meta} />
      </div>
    );
  }

  return (
    <div className="report-root flex flex-col gap-6">
      <PrintHeader account={meta.account_name} number={meta.account_number} period={periodLabel} />

      {/* r10 22/7: auto-fit KPI tiles — no fixed column cap now the shell is full-bleed (print keeps its
          own A4 width, so this still lands ~3-across on paper). */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
        <KpiTile label={eventsLabel} value={formatInt(eventsValue)} />
        <KpiTile
          label={t("customer.reports.kpiParticipants")}
          value={formatInt(kpis.total_participants)}
        />
        <KpiTile
          label={t("customer.reports.kpiMinutes")}
          value={formatInt(kpis.total_minutes)}
          sub={t("customer.reports.approxHours", { hours: formatInt(kpis.total_hours) })}
        />
        <KpiTile
          label={t("customer.reports.kpiActiveHosts")}
          value={kpis.active_hosts != null ? formatInt(kpis.active_hosts) : "—"}
          sub={t("customer.reports.activeHostsHint")}
          title={t("customer.reports.activeHostsHint")}
        />
      </div>

      <Section title={t("customer.reports.trendEvents", { unit: eventsLabel })}>
        <BarChart
          bars={trend.map((b) => ({ label: trendLabel(t, b, meta.bucket), value: b.events }))}
          caption={t("customer.reports.trendEvents", { unit: eventsLabel })}
          unit={eventsLabel}
          tone="brand"
        />
      </Section>
      <Section title={t("customer.reports.trendParticipants")}>
        <BarChart
          bars={trend.map((b) => ({ label: trendLabel(t, b, meta.bucket), value: b.participants }))}
          caption={t("customer.reports.trendParticipants")}
          unit={t("customer.reports.kpiParticipants")}
          tone="neutral"
        />
      </Section>
      <Section title={t("customer.reports.trendMinutes")}>
        <BarChart
          bars={trend.map((b) => ({ label: trendLabel(t, b, meta.bucket), value: b.minutes }))}
          caption={t("customer.reports.trendMinutes")}
          unit={t("customer.reports.kpiMinutes")}
          tone="violet"
        />
      </Section>

      <Section title={t("customer.reports.trendActiveHosts")}>
        <ActiveHostsNotes meta={meta} kpis={kpis} trend={trend} />
        <BarChart
          bars={trend.map((b) => ({
            // A partial period keeps its number but is starred; the note above explains the star.
            label: `${trendLabel(t, b, meta.bucket)}${b.active_hosts_partial ? "*" : ""}`,
            value: b.active_hosts,
          }))}
          caption={t("customer.reports.trendActiveHosts")}
          unit={t("customer.reports.kpiActiveHosts")}
          tone="brand"
        />
      </Section>

      <Section title={t("customer.reports.heatmapMeetings")}>
        <CalendarHeatmap cells={heatmap} metric="events" series={eventsLabel} />
      </Section>
      <Section title={t("customer.reports.heatmapMinutes")}>
        <CalendarHeatmap
          cells={heatmap}
          metric="minutes"
          series={t("customer.reports.kpiMinutes")}
        />
      </Section>

      <Section title={t("customer.reports.perUser")}>
        {meta.top_users_event_coverage != null && meta.top_users_event_coverage < 99 && (
          <FeedNote>
            {t("customer.reports.coverageNote", { pct: meta.top_users_event_coverage })}
          </FeedNote>
        )}
        <ReportTopUsersTable users={top_users} type={type} />
      </Section>

      <PhoneSection phone={data.phone} meta={meta} />

      <p className="hidden border-t border-(--border) pt-3 text-(length:--text-2xs) text-(--text-muted) print:block">
        {t("customer.reports.footer", {
          account: meta.account_name ?? "—",
          number: meta.account_number ?? "—",
          period: periodLabel,
          today: formatDate(new Date().toISOString()),
        })}
      </p>
    </div>
  );
}

/** Coverage notes for the Active hosts section. Deliberately NOT print:hidden — a printed report must
 *  carry the same caveat as the screen. */
function ActiveHostsNotes({
  meta,
  kpis,
  trend,
}: {
  meta: CustomerReport["meta"];
  kpis: CustomerReport["kpis"];
  trend: ReportTrendBucket[];
}) {
  const { t } = useTranslation();
  const from = meta.active_hosts_coverage.from;
  const starred = trend.some((b) => b.active_hosts != null && b.active_hosts_partial);
  let coverage: string | null = null;
  if (from == null) {
    coverage = t("customer.reports.activeHostsNoHistory");
  } else if (kpis.active_hosts == null) {
    coverage = t("customer.reports.activeHostsBeforeCoverage", { from: formatDate(from) });
  } else if (meta.active_hosts_coverage.partial || meta.start < from) {
    coverage = t("customer.reports.activeHostsPartialCoverage", {
      from: formatDate(from),
      end: formatDate(meta.end),
    });
  }
  return (
    <>
      {coverage && <FeedNote>{coverage}</FeedNote>}
      {starred && <FeedNote>{t("customer.reports.activeHostsPartialBuckets")}</FeedNote>}
    </>
  );
}

/**
 * Zoom Phone call activity for the report's range. Hidden when the account has no phone data (or an
 * older API sent no `phone`). KPI tiles: total calls (+ active days), inbound / outbound, call minutes.
 * No users figure (owner ruling 2026-09-19: ZPCD's unique_users counts external numbers too, so "peak
 * users per day" was removed). Stacked inbound/outbound bars per period; a period
 * before the phone floor is a no-data stub, a straddling one is starred. Coverage notes print.
 */
function PhoneSection({
  phone,
  meta,
}: {
  phone: ReportPhone | undefined;
  meta: CustomerReport["meta"];
}) {
  const { t } = useTranslation();
  if (!phone || !phone.has_phone_data) return null;
  const { kpis, trend, coverage } = phone;
  const from = coverage.from;
  let note: string | null = null;
  if (from == null) note = t("customer.reports.phoneNoHistory");
  else if (kpis == null)
    note = t("customer.reports.phoneBeforeCoverage", { from: formatDate(from) });
  else if (coverage.partial || meta.start < from)
    note = t("customer.reports.phonePartialCoverage", {
      from: formatDate(from),
      end: formatDate(meta.end),
    });
  const starred = trend.some((b) => b.call_count != null && b.partial);
  const inbound = t("customer.phone.inbound");
  const outbound = t("customer.phone.outbound");

  return (
    <Section title={t("customer.reports.phoneTitle")}>
      <div className="flex flex-col gap-4">
        {note && <FeedNote>{note}</FeedNote>}
        {starred && <FeedNote>{t("customer.reports.phonePartialBuckets")}</FeedNote>}
        {meta.type !== "both" && <FeedNote>{t("customer.reports.phoneAllTypes")}</FeedNote>}
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
          <KpiTile
            label={t("customer.phone.totalCalls")}
            value={kpis ? formatInt(kpis.total_calls) : "—"}
            sub={kpis ? t("customer.phone.activeDays", { count: kpis.active_days }) : undefined}
          />
          <KpiTile
            label={t("customer.phone.direction")}
            value={kpis ? `${formatInt(kpis.inbound)} / ${formatInt(kpis.outbound)}` : "—"}
            sub={t("customer.phone.inboundOutbound")}
          />
          <KpiTile
            label={t("customer.phone.talkTime")}
            value={kpis ? `${formatInt(kpis.total_minutes)} ${t("customer.phone.min")}` : "—"}
            sub={
              kpis
                ? t("customer.phone.approxHours", {
                    hours: formatInt(Math.round(kpis.total_minutes / 60)),
                  })
                : undefined
            }
          />
        </div>
        <StackedBarChart
          caption={t("customer.reports.phoneTrend")}
          legend={[
            { tone: "info", label: inbound },
            { tone: "warn", label: outbound },
          ]}
          bars={trend.map<ChartBar>((b) => ({
            label: `${bucketLabel(t, b.bucket, meta.bucket)}${b.call_count != null && b.partial ? "*" : ""}`,
            segments: [
              { value: b.inbound_count, tone: "info", label: inbound },
              { value: b.outbound_count, tone: "warn", label: outbound },
            ],
          }))}
        />
      </div>
    </Section>
  );
}

function PrintHeader({
  account,
  number,
  period,
}: {
  account: string | null;
  number: string | null;
  period: string;
}) {
  const { t } = useTranslation();
  return (
    <header className="hidden border-b-2 border-(--text-strong) pb-3 print:block">
      <p className="text-(length:--text-2xs) font-medium uppercase tracking-wide text-(--text-muted)">
        {t("customer.reports.eyebrow")}
      </p>
      <h2 className="text-(length:--text-xl) font-semibold text-(--text-strong)">
        {account ?? "—"}
      </h2>
      <p className="tabular mt-1 text-(length:--text-xs) text-(--text-muted)">
        {t("customer.reports.accountNumber", { number: number ?? "—" })} · {period}
      </p>
    </header>
  );
}

function trendLabel(
  t: ReturnType<typeof useTranslation>["t"],
  b: ReportTrendBucket,
  bucket: "day" | "month",
): string {
  return bucketLabel(t, b.bucket, bucket);
}

/** A trend bucket key ("YYYY-MM-DD" / "YYYY-MM") → its axis label ("5/3" / "Mar 26"). */
function bucketLabel(
  t: ReturnType<typeof useTranslation>["t"],
  key: string,
  bucket: "day" | "month",
): string {
  const parts = key.split("-");
  if (bucket === "month") {
    const [y, m] = parts;
    // A malformed bucket (no separator) must degrade to the raw string — never to a "NaN 26" axis label.
    if (!y || !m) return key;
    return `${monthShort(t, Number(m))} ${y.slice(2)}`;
  }
  const [, m, d] = parts;
  if (!m || !d) return key;
  return `${Number(d)}/${Number(m)}`;
}

/**
 * GitHub-style calendar heatmap over the day-grain cells; intensity = value/max. Columns = weeks, rows =
 * weekdays (Sun at the top). Each in-range day is its own target on the shared chart tooltip
 * (../chartTooltip `useChartTooltip`, wave 5 follow-up 2026-09-19): hover or focus shows THAT day's
 * date + value instantly (no native `title`, which browsers delay ~1 s), the bubble is clamped to the
 * figure and hidden in print. ONE roving tab stop per heatmap (not one per day — a 12-month range had
 * ~365 stops per heatmap); ArrowLeft/Right move a week, ArrowUp/Down a day, Home/End to the first/last
 * day. The figure is the tooltip's positioning box; only the inner grid scrolls sideways, so the bubble
 * is never clipped by the scroll box.
 */
function CalendarHeatmap({
  cells,
  metric,
  series,
}: {
  cells: ReportHeatmapCell[];
  metric: "events" | "minutes";
  /** What the value counts, for the tooltip / aria-label ("2026-01-12 · Meetings: 7"). */
  series: string;
}) {
  const { t } = useTranslation();
  const { figureRef, target, bubble } = useChartTooltip({ rowOrder: "top-down" });
  if (cells.length === 0) return <EmptyState title={t("customer.reports.noHeatmap")} />;

  const valueByDate = new Map(cells.map((c) => [c.date, c[metric]]));
  const max = Math.max(1, ...cells.map((c) => c[metric]));

  const dates = cells.map((c) => c.date).sort();
  const first = new Date(dates[0] + "T00:00:00Z");
  const last = new Date(dates[dates.length - 1] + "T00:00:00Z");
  const gridStart = new Date(first.getTime() - first.getUTCDay() * DAY);

  const weeks: { date: string; value: number | null }[][] = [];
  for (let cur = gridStart.getTime(); cur <= last.getTime(); ) {
    const col: { date: string; value: number | null }[] = [];
    for (let d = 0; d < 7; d++) {
      const day = new Date(cur);
      const ds = isoDate(day);
      const inRange = day >= first && day <= last;
      col.push({ date: ds, value: inRange ? (valueByDate.get(ds) ?? 0) : null });
      cur += DAY;
    }
    weeks.push(col);
  }

  // Minutes shares the trend chart's violet identity — not --warn-solid, which is a status colour
  // (danger/attention) with no meaning here; see tokens.css "Chart-series identity".
  const tone = metric === "events" ? "bg-(--info-solid)" : "bg-(--data-violet-solid)";

  return (
    <figure
      ref={figureRef}
      className="relative flex flex-col gap-2 rounded-(--radius) border border-(--border) bg-(--surface) p-4 shadow-(--shadow-sm)"
    >
      <figcaption className="sr-only">
        {metric === "events"
          ? t("customer.reports.heatmapMeetings")
          : t("customer.reports.heatmapMinutes")}
      </figcaption>
      {/* The scroll box (a long range at phone width). -m-1 p-1 leaves room for the focus ring of the
          edge cells, which a scroll box would otherwise clip. */}
      <div className="-m-1 overflow-x-auto p-1">
        <div className="flex gap-[3px]">
          {weeks.map((col, wi) => (
            <div key={wi} className="flex flex-col gap-[3px]">
              {col.map((cell, di) =>
                cell.value === null ? (
                  <span key={cell.date} className="size-3 rounded-[2px]" />
                ) : (
                  <span
                    key={cell.date}
                    className={cn(
                      "size-3 rounded-[2px]",
                      TARGET_FOCUS,
                      cell.value === 0
                        ? "bg-(--surface-sunken) ring-1 ring-inset ring-(--border-hairline)"
                        : tone,
                    )}
                    style={
                      cell.value === 0 ? undefined : { opacity: 0.2 + 0.8 * (cell.value / max) }
                    }
                    {...target(
                      `h${cell.date}`,
                      { period: cell.date, series, value: formatInt(cell.value) },
                      { col: wi, row: di },
                    )}
                  />
                ),
              )}
            </div>
          ))}
        </div>
      </div>
      {/* sr-only data table so screen-reader / keyboard users get the per-day numbers the color cells encode. */}
      <table className="sr-only">
        <caption>
          {metric === "events"
            ? t("customer.reports.heatmapMeetings")
            : t("customer.reports.heatmapMinutes")}
        </caption>
        <tbody>
          {cells.map((c) => (
            <tr key={c.date}>
              <td>{c.date}</td>
              <td>{c[metric]}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center gap-1.5 text-(length:--text-2xs) text-(--text-muted)">
        <span>{t("customer.reports.less")}</span>
        {[0.2, 0.45, 0.7, 1].map((o) => (
          <span key={o} className={`size-3 rounded-[2px] ${tone}`} style={{ opacity: o }} />
        ))}
        <span>{t("customer.reports.more")}</span>
      </div>
      {bubble}
    </figure>
  );
}
